import { useEffect, useState, CSSProperties } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { Text, Input } from '@hhgtech/hhg-components/mantine'
import { useIntl } from 'react-intl'

import { InputError } from 'components/pages/healthTools/pwg/form'
import { FormControl } from 'components/pages/healthTools/pwg/index.styled'
import { PregWeightInfo } from 'interfaces/types'

import { StyledContainer } from './index.styled'

export type Props = {
  info: PregWeightInfo
  errorMessage?: InputError
  onChange?: (o: PregWeightInfo) => void
  style?: CSSProperties
  className?: string
  isMobile?: boolean
}

const PWGInputForm = ({
  info,
  className,
  style,
  onChange,
  errorMessage = {} as InputError,
}: Props) => {
  const { formatMessage: f } = useIntl()

  const [isEdit, setIsEdit] = useState({
    weightBefore: false,
    height: false,
    weight: false,
  })

  const checkValidInputIsANumber = (s: string, cb: (a: string) => void) => {
    if (s === '' || (!isNaN(s as any) && !isNaN(parseFloat(s)))) {
      cb(s)
    }
  }

  const onWeightBeforeChanged = (weightBefore: string) => {
    checkValidInputIsANumber(weightBefore, (a) => {
      setIsEdit((value) => ({
        ...value,
        weightBefore: true,
      }))
      const newInfo: PregWeightInfo = { ...info, weightBefore: a }
      onChange && onChange(newInfo)
    })
  }
  const onHeightChanged = (height: string) => {
    checkValidInputIsANumber(height, (h) => {
      setIsEdit((value) => ({
        ...value,
        height: true,
      }))
      const newInfo: PregWeightInfo = { ...info, height: h }
      onChange && onChange(newInfo)
    })
  }
  const onWeightChanged = (weight: string) => {
    checkValidInputIsANumber(weight, (w) => {
      setIsEdit((value) => ({
        ...value,
        weight: true,
      }))
      const newInfo: PregWeightInfo = { ...info, weight: w }
      onChange && onChange(newInfo)
    })
  }

  useEffect(() => {
    onChange &&
      onChange({
        ...info,
      } as PregWeightInfo)
  }, [])

  useEffect(() => {
    setIsEdit({
      weightBefore: false,
      height: false,
      weight: false,
    })
  }, [errorMessage])

  return (
    <StyledContainer style={style} className={className}>
      <FormControl>
        <Input.Number
          label={
            <Text size="s4" as="label">
              {f({
                id: 'pregnancyWeightGain.weightBeforeInput',
                defaultMessage: 'What was your weight before pregnancy?',
              })}{' '}
              <Text size="s4" color={theme.colors.gray300} as="span">
                {f({
                  id: 'pregnancyWeightGain.weightInput.desc',
                  defaultMessage: ' (kg)',
                })}
              </Text>
            </Text>
          }
          error={!isEdit.weightBefore ? errorMessage.weightBefore : ''}
          name="pwg-weightBefore"
          onChange={(v) => onWeightBeforeChanged(String(v))}
          value={
            info.weightBefore !== '' ? Number(info.weightBefore) : undefined
          }
          precision={1}
          inputMode="decimal"
          min={0}
        />
      </FormControl>
      <FormControl>
        <Input.Number
          label={
            <Text size="s4" as="label">
              {f({
                id: 'pregnancyWeightGain.heightInput',
                defaultMessage: 'What is your height?',
              })}{' '}
              <Text size="s4" color={theme.colors.gray300} as="span">
                {f({
                  id: 'bmi.heightInput.desc',
                  defaultMessage: ' (cm)',
                })}
              </Text>
            </Text>
          }
          error={!isEdit.height ? errorMessage.height : ''}
          name="pwg-height"
          value={info.height !== '' ? Number(info.height) : undefined}
          onChange={(v) => onHeightChanged(String(v))}
          min={0}
        />
      </FormControl>
      <FormControl>
        <Input.Number
          label={
            <Text size="s4" as="label">
              {f({
                id: 'pregnancyWeightGain.weightInput',
                defaultMessage: 'How much do you weigh now?',
              })}{' '}
              <Text size="s4" color={theme.colors.gray300} as="span">
                {f({
                  id: 'pregnancyWeightGain.weightInput.desc',
                  defaultMessage: ' (kg)',
                })}
              </Text>
            </Text>
          }
          error={!isEdit.weight ? errorMessage.weight : ''}
          name="pwg-weight"
          value={info.weight !== '' ? Number(info.weight) : undefined}
          onChange={(v) => onWeightChanged(String(v))}
          precision={1}
          inputMode="decimal"
          min={0}
        />
      </FormControl>
    </StyledContainer>
  )
}

export { PWGInputForm }
